import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { IAgoraRTCClient, IAgoraRTCRemoteUser, ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../api/keys';
import { api, ApiError } from '../../lib/api';
import type { CallToken, Session } from '../../types/api';

function numericUid(id: string): number {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return hash;
}

function RemoteVideo({ user, name }: { user: IAgoraRTCRemoteUser; name: string }) {
  const container = useRef<HTMLDivElement>(null);
  const track = user.videoTrack;
  useEffect(() => {
    if (container.current && track) track.play(container.current);
    return () => { track?.stop(); };
  }, [track]);
  return <div className="relative h-64 overflow-hidden rounded-lg bg-black text-white">
    <div ref={container} className="absolute inset-0" />
    {!user.hasVideo && <div className="flex h-full items-center justify-center p-4 text-center">{name} · camera off</div>}
    <span className="absolute bottom-2 start-2 rounded bg-black/70 px-2 py-1 text-xs">{name}</span>
  </div>;
}

export function AdminLessonRoom({ session }: { session: Session }): ReactElement {
  const [phase, setPhase] = useState<'idle' | 'joining' | 'joined'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const client = useRef<IAgoraRTCClient | null>(null);
  const mic = useRef<IMicrophoneAudioTrack | null>(null);
  const camera = useRef<ICameraVideoTrack | null>(null);
  const preview = useRef<HTMLDivElement>(null);
  const attempt = useRef(0);
  // Admin sockets do not receive participant-only lesson events. Refresh the
  // shared detail query so cancellation and the join-window boundary close this call.
  useQuery({ queryKey: qk.adminSession(session.id), queryFn: () => api.get<Session>(`/admin/sessions/${session.id}`),
    refetchInterval: ['scheduled', 'in_progress'].includes(session.status) ? 15000 : false });
  const release = useCallback(async () => {
    attempt.current++;
    const active = client.current;
    client.current = null;
    mic.current?.close(); camera.current?.close();
    mic.current = null; camera.current = null;
    if (active) { active.removeAllListeners(); await active.leave().catch(() => undefined); }
  }, []);
  useEffect(() => () => { void release(); }, [release]);
  useEffect(() => {
    if (!session.isJoinable || !['scheduled', 'in_progress'].includes(session.status)) {
      void release(); setPhase('idle'); setUsers([]); setMicOn(false); setCameraOn(false);
    }
  }, [session.status, session.isJoinable, release]);

  const join = async () => {
    if (phase !== 'idle') return;
    setPhase('joining'); setError(null);
    const current = ++attempt.current;
    try {
      const token = await api.post<CallToken>(`/admin/sessions/${session.id}/call-token`);
      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
      if (attempt.current !== current) return;
      const rtc = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      client.current = rtc;
      const refresh = () => { if (client.current === rtc) setUsers([...rtc.remoteUsers]); };
      rtc.on('user-joined', refresh); rtc.on('user-left', refresh); rtc.on('user-unpublished', refresh);
      rtc.on('user-published', async (user, mediaType) => {
        try {
          await rtc.subscribe(user, mediaType);
          if (client.current !== rtc) return;
          if (mediaType === 'audio') user.audioTrack?.play();
          refresh();
        } catch { if (client.current === rtc) setError('Could not play participant media. Leave and rejoin to try again.'); }
      });
      rtc.on('token-privilege-will-expire', async () => {
        try {
          const renewed = await api.post<CallToken>(`/admin/sessions/${session.id}/call-token`);
          if (client.current === rtc) await rtc.renewToken(renewed.token);
        } catch { if (client.current === rtc) { setError('Call access expired. Leave and rejoin the lesson.'); await release(); setPhase('idle'); setUsers([]); setMicOn(false); setCameraOn(false); } }
      });
      await rtc.join(token.appId, token.channelName, token.token, token.uid);
      if (attempt.current !== current) { await rtc.leave(); return; }
      refresh(); setPhase('joined');
    } catch (err) {
      if (attempt.current !== current) return;
      await release(); setPhase('idle');
      setError(err instanceof ApiError ? err.message : 'Could not join the call. Check your connection and try again.');
    }
  };
  const toggle = async (kind: 'mic' | 'camera') => {
    const rtc = client.current;
    if (!rtc || busy) return;
    setBusy(true); setError(null);
    const current = attempt.current;
    try {
      const AgoraRTC = (await import('agora-rtc-sdk-ng')).default;
      if (kind === 'mic') {
        let track = mic.current;
        if (!track) {
          track = await AgoraRTC.createMicrophoneAudioTrack();
          if (attempt.current !== current) { track.close(); return; }
          mic.current = track;
          await rtc.publish(track);
        } else await track.setEnabled(!micOn);
        if (attempt.current === current) setMicOn(!micOn);
      } else {
        let track = camera.current;
        if (!track) {
          track = await AgoraRTC.createCameraVideoTrack();
          if (attempt.current !== current) { track.close(); return; }
          camera.current = track;
          await rtc.publish(track);
        } else await track.setEnabled(!cameraOn);
        if (attempt.current === current) { setCameraOn(!cameraOn); if (!cameraOn && preview.current) track.play(preview.current); }
      }
    } catch {
      if (attempt.current === current) {
        if (kind === 'mic' && !micOn) { mic.current?.close(); mic.current = null; }
        if (kind === 'camera' && !cameraOn) { camera.current?.close(); camera.current = null; }
        setError('Could not enable your device. Check browser permissions and try again.');
      }
    }
    finally { setBusy(false); }
  };
  const leave = async () => { await release(); setPhase('idle'); setUsers([]); setMicOn(false); setCameraOn(false); };
  return <section className="panel mb-4 space-y-4 p-4" aria-label="Administrator lesson call">
    <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-semibold">Join as administrator</h2><p className="mt-1 text-sm text-ink-2">Your administrator presence is visible to participants. You join with your microphone and camera off.</p></div>
      {phase === 'idle' ? <button type="button" className="btn btn-primary" disabled={!session.isJoinable} onClick={() => { void join(); }}>Join lesson as admin</button> : <button type="button" className="btn btn-danger" onClick={() => { void leave(); }}>{phase === 'joining' ? 'Cancel joining' : 'Leave lesson'}</button>}
    </div>
    {!session.isJoinable && phase === 'idle' && <p className="text-sm text-ink-3">The room opens 10 minutes before the lesson and closes 30 minutes after it ends.</p>}
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
    {phase === 'joining' && <p role="status">Connecting to lesson…</p>}
    {phase === 'joined' && <>
      <p role="status" className="text-sm">Connected as administrator</p>
      <div className="grid gap-3 sm:grid-cols-2">{users.map((user) => <RemoteVideo key={user.uid} user={user} name={user.uid === numericUid(session.student.id) ? `${session.student.fullName} · student` : user.uid === numericUid(session.tutor.id) ? `${session.tutor.fullName} · tutor` : 'Administrator'} />)}</div>
      {!users.length && <p className="text-sm text-ink-2">Waiting for the student and tutor to join.</p>}
      <div ref={preview} className={`h-40 overflow-hidden rounded bg-black ${cameraOn ? '' : 'hidden'}`} role="img" aria-label="Your camera preview" />
      <div className="flex flex-wrap gap-2"><button type="button" className="btn btn-secondary" aria-pressed={micOn} disabled={busy} onClick={() => { void toggle('mic'); }}>{micOn ? 'Mute microphone' : 'Enable microphone'}</button><button type="button" className="btn btn-secondary" aria-pressed={cameraOn} disabled={busy} onClick={() => { void toggle('camera'); }}>{cameraOn ? 'Turn camera off' : 'Enable camera'}</button></div>
    </>}
  </section>;
}
